<template>
  <section>
    <div class="row">
      <div class="col-2">
        <b-field>
          <b-checkbox v-model="currentTeam">
            {{ currentTeam ? 'Choose current team' : 'Choose specific team' }}
          </b-checkbox>
        </b-field>
        <b-field>
          <b-checkbox v-model="currentPosition">
            {{
              currentPosition
                ? 'Choose current position'
                : 'Choose specific position'
            }}
          </b-checkbox>
        </b-field>
        <b-button @click="setUserIntakes">Set user intakes and teams</b-button>
      </div>
      <div class="col-4">
        <b-field label="Intake">
          <multiselect
            v-model="intakes"
            :options="activeIntakes"
            track-by="id"
            label="name"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="true"
            :preserve-search="true"
            placeholder="Choose one or multiple intakes"
            :preselect-first="false"
          >
          </multiselect>
        </b-field>
      </div>
      <div v-if="!currentTeam" class="col-4">
        <b-field label="Team">
          <multiselect
            v-model="selectedTeam"
            :options="teams"
            track-by="id"
            label="value"
            :multiple="false"
            :close-on-select="true"
            :clear-on-select="true"
            :preserve-search="true"
            placeholder="Choose one team"
            :preselect-first="false"
          >
          </multiselect>
        </b-field>
      </div>
      <div v-if="!currentPosition" class="col-2">
        <b-field label="Position">
          <multiselect
            v-model="selectedPosition"
            :options="positionsAutocomplete"
            track-by="value"
            label="value"
            :multiple="false"
            :close-on-select="true"
            :clear-on-select="true"
            :preserve-search="true"
            placeholder="Choose one position"
            :preselect-first="false"
          >
          </multiselect>
        </b-field>
      </div>
    </div>

    <b-table
      :checkable="isAdmin"
      :checked-rows.sync="checkedRows"
      class="finance-table"
      :data="users"
      sticky-header
      sticky-checkbox
      striped
      mobile-cards
      paginated
      per-page="250"
    >
      <b-table-column
        v-for="(column, index) in allColumns"
        :key="index"
        :label="column.title"
        :header-class="column.field === 'team' ? 'team-column' : ''"
        class="column"
        v-slot="props"
      >
        <span v-if="column.field === 'team'">
          <div v-if="props.row[column.field] !== noTeam">
            <div v-for="(team, index) in props.row[column.field]" :key="index">
              <p class="one-row">
                {{ team.name }} - {{ team.position }}
                <b-icon
                  size="small"
                  type="is-success"
                  :icon="icons[team.positionIndex]"
                ></b-icon>
              </p>
            </div>
          </div>
          <span v-else>{{ props.row[column.field] }}</span>
        </span>
        <div v-else-if="column.field === 'intake'">
          <b-icon
            v-if="
              !props.row.activeIntakes.length ||
              !props.row.activeIntakes.includes(column.title)
            "
            size="small"
            :type="
              isCreatedInIntake(column.title, props.row.createdAt)
                ? 'is-warning'
                : 'is-danger'
            "
            :icon="
              isCreatedInIntake(column.title, props.row.createdAt)
                ? 'help'
                : 'close'
            "
          ></b-icon>
          <div v-else>
            <div
              v-for="(team, index) in props.row.teamIntakes.filter(
                (teamRow) => teamRow.intakeName === column.title
              )"
              :key="index"
            >
              <p class="one-row">
                {{ team.teamName }}
                <b-icon
                  size="small"
                  type="is-success"
                  :icon="icons[team.positionIndex]"
                ></b-icon>
                <b-icon
                  icon="delete"
                  class="cursor-pointer mt-1 ml-1"
                  type="is-warning"
                  @click.native.prevent="deleteUserTeamIntake(team.id)"
                ></b-icon>
              </p>
            </div>
          </div>
        </div>
        <span v-else> {{ props.row[column.field] }}</span>
      </b-table-column>
    </b-table>

    <div class="row">
      <b-button @click="generateFinanceRecord">Generate records</b-button>
    </div>
    <div class="row">
      <b-field label="Filter by Intake">
        <multiselect
          v-model="selectedIntake"
          :options="activeIntakes"
          track-by="id"
          label="name"
          :multiple="false"
          :close-on-select="true"
          :clear-on-select="true"
          :preserve-search="true"
          placeholder="Choose an intake"
          :preselect-first="false"
        >
        </multiselect>
      </b-field>
    </div>

    <b-table
      v-if="filteredPayments.length"
      :data="filteredPayments"
      striped
      hoverable
      mobile-cards
      paginated
      per-page="100"
      detailed
      detail-key="id"
      show-detail-icon
    >
      <b-table-column v-slot="props" field="username" label="User" sortable>
        {{ props.row.username }}
      </b-table-column>
      <b-table-column v-slot="props" field="position" label="Position" sortable>
        {{ props.row.position }}
      </b-table-column>
      <b-table-column
        v-slot="props"
        field="position"
        label="Outstanding / Total"
        sortable
      >
        {{ props.row.outstanding }} / {{ props.row.total }}
      </b-table-column>
      <template #detail="props">
        <div class="content columns"></div>
      </template>
    </b-table>
  </section>
</template>

<script>
import moment from 'moment';
import Multiselect from 'vue-multiselect';

import UtilService from '../services/util';
import TeamService from '../services/team.service';
import financeService from '../services/finance.service';

export default {
  name: 'Finance',
  components: {
    Multiselect,
  },
  data: () => ({
    users: [],
    selectedIntake: null,
    filteredPayments: [],
    intakes: [],
    checkedRows: [],
    selectedTeam: [],
    selectedPosition: [],
    currentTeam: true,
    currentPosition: true,
    teams: [],
    positions: ['Advisor', 'TL', 'Manager', 'Admin'],
    icons: ['account', 'account-multiple', 'account-group', 'account-star'],
    noTeam: 'No team',
    currency: '£',
  }),
  computed: {
    isAdmin() {
      return UtilService.isAdmin();
    },
    positionsAutocomplete() {
      return UtilService.generateFilterArray(this.positions);
    },
    activeIntakes() {
      return this.$store.state.application_filter.intakes;
    },
    dynamicColumns() {
      return this.activeIntakes.map((intake) => ({
        field: 'intake',
        title: intake.name,
        id: intake.id,
      }));
    },

    allColumns() {
      return [
        { field: 'username', title: 'Name' },
        { field: 'team', title: 'Team', class: "'large'" },
        ...this.dynamicColumns,
      ];
    },
  },
  async mounted() {
    await TeamService.getTeams().then((response) => {
      this.teams = response.data.map((team) => ({
        value: team.name,
        ...team,
      }));
    });

    await this.getUsers();
    const currentIntake = this.activeIntakes[0].name;
    await financeService
      .financeRecordsForIntake({
        intake: currentIntake,
      })
      .then((response) => {
        this.filteredPayments = response.data.map((payment) => ({
          id: payment.id,
          amount: parseInt(payment.amount, 10),
          student: payment.student
            ? UtilService.studentName(payment.student)
            : '',
          teamId: payment.teamId,
          intake: payment.intake,
          userId: payment.userId,
          username: payment.user_payment_info
            ? payment.user_payment_info.username
            : '',
          position: payment.position,
          status: payment.status,
          type: payment.type,
        }));
        const userPaymentsMap = new Map();

        this.filteredPayments.forEach((payment) => {
          if (!userPaymentsMap.has(payment.userId)) {
            const allUserPositions = this.filteredPayments
              .filter((p) => p.userId === payment.userId)
              .map((p) => p.position);
            const position = allUserPositions.includes('Admin')
              ? 'Admin'
              : allUserPositions.includes('Manager')
              ? 'Manager'
              : allUserPositions.includes('TL')
              ? 'TL'
              : 'Advisor';
            userPaymentsMap.set(payment.userId, {
              ...payment,
              position,
              total: payment.amount,
              outstanding:
                payment.status === 'Outstanding' ? payment.amount : 0,
              additionalPaymentInfo: [payment],
            });
          } else {
            userPaymentsMap
              .get(payment.userId)
              .additionalPaymentInfo.push(payment);
            userPaymentsMap.get(payment.userId).total += payment.amount;
            if (payment.status === 'Outstanding') {
              userPaymentsMap.get(payment.userId).outstanding += payment.amount;
            }
          }
        });

        // Sort the list so that the managers are first, then the TL, and then the advisors
        this.filteredPayments = Array.from(userPaymentsMap.values()).sort(
          (a, b) => {
            const positionOrder = ['Manager', 'TL', 'Advisor'];
            return (
              positionOrder.indexOf(a.position) -
              positionOrder.indexOf(b.position)
            );
          }
        );

        // this.filteredPayments = Array.from(userPaymentsMap.values());
      });
  },
  methods: {
    getTeamName(user) {
      return user.team_users && user.team_users.length
        ? user.team_users.map((team) => {
            return {
              isManager: team.is_manager,
              isTeamLeader: user.isTeamLeader,
              teamId: team.team.id,
              name: team.team.name,
            };
          })
        : 'No team';
    },

    async getUsers() {
      await financeService.getAllUsers().then((response) => {
        this.users = response.data.map((user) => {
          let teamIntakes = user.user_team_intakes
            ? user.user_team_intakes.map((intakeTeam) => {
                return {
                  id: intakeTeam.id,
                  intakeId: intakeTeam.intake.id,
                  intakeName: intakeTeam.intake.name,
                  teamName: intakeTeam.team.name,
                  teamId: intakeTeam.team.id,
                  position: intakeTeam.position,
                  positionIndex: this.positions.indexOf(intakeTeam.position),
                };
              })
            : null;
          const team =
            user.team_users && user.team_users.length
              ? user.team_users.map((team) => {
                  const positionIndex = team.isManager
                    ? 2
                    : user.isTeamLeader
                    ? 1
                    : 0;
                  return {
                    isManager: team.isManager,
                    isTeamLeader: user.isTeamLeader,
                    teamId: team.team.id,
                    name: team.team.name,
                    positionIndex,
                    position: this.positions[positionIndex],
                  };
                })
              : this.noTeam;

          teamIntakes = teamIntakes.filter((v) => v !== null);
          return {
            ...user,
            team,
            teamIntakes,
            activeIntakes: teamIntakes
              ? teamIntakes.map((intake) => intake.intakeName)
              : null,
          };
        });
      });
    },

    isCreatedInIntake(intake, createdDate) {
      const intakePeriods = intake.split('/');
      const startMonth = 'January 2001';
      const endMonth = intakePeriods[intakePeriods.length - 1];
      const createdMoment = moment(createdDate);
      const startMoment = moment(`${startMonth}`, 'MMMM YYYY').startOf('month');
      const endMoment = moment(`${endMonth}`, 'MMMM YYYY').endOf('month');
      return createdMoment.isBetween(startMoment, endMoment, null, '[]');
      // return intakePeriods.some((period) => {

      // });
    },

    generateFinanceRecord() {
      financeService.generateFinance().then((response) => {
        this.$buefy.toast.open({
          duration: 3000,
          message: 'Finance records have been generated',
          position: 'is-bottom',
          type: 'is-success',
        });
        this.filteredPayments = response.data;
      });
    },

    deleteUserTeamIntake(id) {
      financeService.deleteUserTeamIntake(id).then(() => {
        this.$buefy.toast.open({
          duration: 3000,
          message: 'User intakes position has been deleted',
          position: 'is-bottom',
          type: 'is-success',
        });
        this.getUsers();
      });
    },

    setUserIntakes() {
      let message = null;
      if (this.intakes.length === 0) {
        message = 'Please choose an intake';
      } else if (!this.currentTeam && this.selectedTeam.length === 0) {
        message = 'Please choose a team';
      } else if (!this.currentPosition && this.selectedPosition.length === 0) {
        message = 'Please choose a position';
      } else if (this.checkedRows.length === 0) {
        message = 'Please select at least one user';
      }

      if (message) {
        this.$buefy.toast.open({
          duration: 3000,
          message,
          position: 'is-bottom',
          type: 'is-danger',
        });
        return;
      }
      const userTeamIntake = this.checkedRows
        .map((user) => {
          return this.intakes.map((intake) => {
            if (!user.team) {
              return {
                userId: user.id,
                intakeId: intake.id,
                teamId: null,
                position: null,
              };
            }
            return user.team.map((team) => {
              return {
                userId: user.id,
                intakeId: intake.id,
                teamId: this.currentTeam ? team.teamId : this.selectedTeam.id,
                position: this.currentPosition
                  ? team.position
                  : this.selectedPosition.value,
              };
            });
          });
        })
        .flat()
        .flat();

      financeService.bulkUpdate(userTeamIntake).then(() => {
        this.getUsers();
        this.$buefy.toast.open({
          duration: 3000,
          message: 'User intakes and teams have been set',
          position: 'is-bottom',
          type: 'is-success',
        });
      });
    },
  },
};
</script>

<style lang="css">
.finance-table {
  /* overflow-y: scroll; */
}

.finance-table .team-column {
  min-width: 200px;
}

.b-table.finance-table .table-wrapper.has-sticky-header {
  max-height: 30vh !important;
}

.one-row {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
</style>
